import { unicodeBold } from "@/components/Rich";
import { useEffect, useState } from "react";
import { Copy, CheckCircle, ShareNetwork, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, act } from "@/lib/api";
import { Badge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Media } from "@/components/studio/kit";
import { CarouselViewer } from "@/components/studio/CarouselViewer";

const LABEL = { linkedin: "LinkedIn", x: "X", facebook: "Facebook", instagram: "Instagram" };

export const SocialPostCard = ({ p, onChanged }) => {
  const [caps, setCaps] = useState(p.captions);
  useEffect(() => setCaps(p.captions), [p.captions]);
  const dirty = JSON.stringify(caps) !== JSON.stringify(p.captions);
  const save = async () => { await act(() => api.put(`/studio/social/${p.id}`, { captions: caps }), "Saved & claims re-checked"); onChanged(); };
  const approve = async () => { await act(() => api.post(`/studio/social/${p.id}/approve`), "Approved"); onChanged(); };
  const publish = async () => {
    const r = await act(() => api.post(`/studio/social/${p.id}/publish`, {}));
    if (r) Object.entries(r.publish_results || {}).forEach(([n, v]) => (v.status === "published" ? toast.success(`${LABEL[n]}: published`) : toast.message(`${LABEL[n]}: ${v.detail || v.status}`)));
    onChanged();
  };
  const copy = (n) => { navigator.clipboard.writeText(unicodeBold(caps[n])); toast.success(`${LABEL[n]} caption copied`); };
  const bad = p.claim_check?.unsupported || [];
  return (
    <div className="border border-border rounded-md bg-[#121212] overflow-hidden rise" data-testid={`social-post-${p.id}`}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
        <div className="text-sm font-semibold truncate flex-1">{p.brief}</div>
        {p.format === "carousel" && <Badge v={`carousel · ${p.slides?.length || p.asset_ids?.length}`} tone="blue" />}
        <Badge v={p.status} />{bad.length ? <Badge v={`unsupported: ${bad.join(", ")}`} tone="red" /> : <Badge v="claims grounded" tone="green" />}
      </div>
      <div className={`grid ${p.format === "carousel" ? "md:grid-cols-[300px_1fr]" : "md:grid-cols-[220px_1fr]"}`}>
        <div className="border-r border-border bg-black">
          {p.format === "carousel" ? <CarouselViewer p={p} /> : <>
          {p.asset ? <Media a={p.asset} className="w-full aspect-square" /> : <div className="aspect-square flex items-center justify-center text-xs text-zinc-600">Text-only post</div>}
          {p.asset?.status === "ready" && <a href={`${p.asset.url}&download=1`} data-testid={`social-download-${p.id}`} className="flex items-center gap-1 justify-center text-xs py-2 text-zinc-400 hover:text-white"><DownloadSimple /> Download visual</a>}
          </>}
        </div>
        <div className="p-4 space-y-3">
          {p.networks.map((n) => (
            <div key={n}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-400">{LABEL[n]}{n === "x" && ` · ${(caps[n] || "").length}/280`}</span>
                <div className="flex items-center gap-1">
                  {p.publish_results?.[n] && <Badge v={p.publish_results[n].status} />}
                  <Button size="sm" variant="ghost" className="h-6 text-xs gap-1" data-testid={`social-copy-${n}-${p.id}`} onClick={() => copy(n)}><Copy /> Copy</Button>
                </div>
              </div>
              <Textarea rows={n === "linkedin" ? 5 : 3} data-testid={`social-caption-${n}-${p.id}`} value={caps[n] || ""} onChange={(e) => setCaps({ ...caps, [n]: e.target.value })} className="bg-black text-xs leading-relaxed" />
            </div>
          ))}
          <div className="flex flex-wrap gap-2 pt-1">
            {dirty && <Button size="sm" variant="secondary" data-testid={`social-save-${p.id}`} onClick={save}>Save edits</Button>}
            {p.status === "draft" && !dirty && <Button size="sm" data-testid={`social-approve-${p.id}`} onClick={approve} className="gap-1 bg-[#00C781] text-black hover:bg-[#00C781]/80"><CheckCircle /> Approve</Button>}
            {["approved", "partially_published"].includes(p.status) && !dirty && <Button size="sm" data-testid={`social-publish-${p.id}`} onClick={publish} className="gap-1 bg-[#007AFF] hover:bg-[#3395FF]"><ShareNetwork /> Publish to connected networks</Button>}
          </div>
        </div>
      </div>
    </div>
  );
};
